import { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import { GlobalContext } from "../contextProvider/ContextProvider";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../styles/SidebarNav.scss";

const NavItemButton = ({ className, isCurrent, onClick, iconClassName, children, ariaLabel, ariaExpanded }) => (
    <button
        type="button"
        className={className}
        onClick={onClick}
        aria-current={isCurrent ? "page" : undefined}
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded}
    >
        {iconClassName ? <i className={iconClassName} aria-hidden="true" /> : null}
        {children}
    </button>
);

NavItemButton.propTypes = {
    className: PropTypes.string.isRequired,
    isCurrent: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    iconClassName: PropTypes.string,
    children: PropTypes.node,
    ariaLabel: PropTypes.string,
    ariaExpanded: PropTypes.bool
};

NavItemButton.defaultProps = {
    isCurrent: false,
    iconClassName: "",
    children: null,
    ariaLabel: undefined,
    ariaExpanded: undefined
};

// Helper function to check if user has edit permission in shared groups
const hasUserEditPermissionInSharedGroups = (sharedGroups, userGuid) => {
    return sharedGroups.some((group) => {
        const share = group.groupShareDto;
        return (
            share &&
            Number(share.guid) === Number(userGuid) &&
            share.gradingCriteriaShares?.some((criteria) => criteria.permission === "EDIT")
        );
    });
};

const TeacherSidebarMenu = ({ state, dispatch, basePath, teacherGuid }) => {
    const { state: globalState } = useContext(GlobalContext);
    const { currentTimePeriodDto, userDetails, token, viewTimePeriodDto, schoolYearDto } = globalState || {};
    const effectiveTeacherGuid = teacherGuid || userDetails?.uid;
    const { gradingProfiles, navCollapsed, selectedLink } = state;
    const [hasSharedGroupsWithEditPermission, setHasSharedGroupsWithEditPermission] = useState(false);

    // Use view key if provided (user switched tab) or fallback to global current
    const activeTimePeriodKey = viewTimePeriodDto?.key || currentTimePeriodDto?.key;

    const gradingProfilesCurrentSem = (gradingProfiles || []).filter(
        (p) => Number(p.timePeriod) === Number(activeTimePeriodKey)
    );
    const currentTimePeriodKey = currentTimePeriodDto?.key ?? currentTimePeriodDto?.timePeriodKey;
    const viewTimePeriodKey = viewTimePeriodDto?.key ?? viewTimePeriodDto?.timePeriodKey;

    const isHistoricalSemester =
        currentTimePeriodKey != null &&
        viewTimePeriodKey != null &&
        String(currentTimePeriodKey) !== String(viewTimePeriodKey);

    const allowSubNav = !isHistoricalSemester;

    const navigate = useNavigate();

    const handleOnClick = (linkName, studentNumb) => {
        dispatch({ type: "SET_SELECTED_LINK", payload: linkName });

        let path;

        if (studentNumb) {
            const studentPath = `/summary/input-ratings/student/${studentNumb}`;
            path = basePath ? `${basePath}${studentPath}` : `/home${studentPath}`;
            navigate(path);
            return;
        }

        const subPath = linkName.replace("HOME", "").toLowerCase();

        if (basePath) {
            path = `${basePath}${subPath}`;
        } else {
            path = `/home${subPath}`;
        }

        navigate(path);
    };

    const handleInputRatingsClick = (linkName) => {
        const targetLink =
            gradingProfilesCurrentSem && gradingProfilesCurrentSem.length > 0
                ? linkName
                : "HOME/SUMMARY/INPUT-RATINGS/GROUPS";
        handleOnClick(targetLink);
    };

    const handleManageSharesClick = () => {
        dispatch({ type: "SET_SELECTED_LINK", payload: "HOME/SUMMARY/SHARE" });

        let path;
        const subPath = "/summary/share";

        if (Array.isArray(gradingProfilesCurrentSem) && gradingProfilesCurrentSem.length > 0) {
            const firstId = gradingProfilesCurrentSem[0]?.key;
            if (basePath) {
                path = `${basePath}${subPath}/${String(firstId)}`;
            } else {
                path = `/home${subPath}/${String(firstId)}`;
            }
        } else if (basePath) {
            path = `${basePath}${subPath}`;
        } else {
            path = `/home${subPath}`;
        }
        navigate(path);
    };

    useEffect(() => {
        if (!effectiveTeacherGuid || !token || !schoolYearDto?.key || !currentTimePeriodDto?.key) return;

        const options = {
            action: "getAllSharedGroups",
            params: {
                teacherGuid: effectiveTeacherGuid,
                schoolYearKey: schoolYearDto.key,
                timePeriod: currentTimePeriodDto.key
            },
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const hasEditPermission = hasUserEditPermissionInSharedGroups(
                        response.data.payload,
                        effectiveTeacherGuid
                    );
                    setHasSharedGroupsWithEditPermission(hasEditPermission);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, [schoolYearDto?.key, currentTimePeriodDto?.key, token, effectiveTeacherGuid]);

    const showSummary = allowSubNav && gradingProfilesCurrentSem && gradingProfilesCurrentSem.length > 0;
    const showDifferentiation =
        allowSubNav && (gradingProfilesCurrentSem.length > 0 || hasSharedGroupsWithEditPermission);

    const isSummarySelected = selectedLink === "HOME/SUMMARY";

    const isSummaryChildSelected =
        selectedLink.includes("HOME/SUMMARY/ROSTER") ||
        selectedLink.includes("SHARE") ||
        selectedLink.includes("DIFFERENTIATION") ||
        selectedLink.includes("INPUT-RATINGS");

    const isDifferentiationSelected = selectedLink.includes("HOME/SUMMARY/DIFFERENTIATION");

    let summaryRowClass = "not-selected-row";

    if (isSummarySelected) {
        summaryRowClass = "selected-row";
    } else if (isSummaryChildSelected) {
        summaryRowClass = "child-selected";
    }
    const profileWithStudents = gradingProfilesCurrentSem.find((p) => p.gradingProfileStudents?.length > 0);
    const sortedHomeroomStudents = useMemo(() => {
        const students = profileWithStudents?.gradingProfileStudents;

        const list = Array.isArray(students) ? [...students] : [];

        return list.sort((a, b) => {
            const lastA = (a.lastName || "").toLowerCase();
            const lastB = (b.lastName || "").toLowerCase();

            if (lastA !== lastB) return lastA.localeCompare(lastB);

            const firstA = (a.firstName || "").toLowerCase();
            const firstB = (b.firstName || "").toLowerCase();

            return firstA.localeCompare(firstB);
        });
    }, [profileWithStudents]);

    const firstStudentNumber = sortedHomeroomStudents?.[0]?.studentNumber ?? null;
    const showStudent = firstStudentNumber || (selectedLink.includes("STUDENT") && !selectedLink.includes("SEARCH"));
    const isSummaryActive = () => {
        return (
            selectedLink === "HOME/SUMMARY" ||
            selectedLink.includes("HOME/SUMMARY/ROSTER") ||
            selectedLink.includes("HOME/SUMMARY/SHARE") ||
            selectedLink.includes("HOME/SUMMARY/DIFFERENTIATION")
        );
    };
    return !navCollapsed ? (
        <nav className="expanded-nav-container" aria-label="Primary navigation">
            {/* Collapse control */}
            <div className="expanded-nav-arrow-row">
                <button
                    type="button"
                    className="nav-toggle-button"
                    onClick={() => dispatch({ type: "TOGGLE_NAV" })}
                    aria-label="Collapse navigation"
                    aria-expanded={!navCollapsed}
                    style={{ textAlign: "right" }}
                >
                    <i className="bi bi-arrow-bar-left white arrow-icon" aria-hidden="true" />
                </button>
            </div>

            <ul className="nav-list">
                {/* Home */}
                <li>
                    <NavItemButton
                        className={`expanded-nav-row ${selectedLink === "HOME" ? "selected-row" : "not-selected-row"}`}
                        onClick={() => handleOnClick("HOME")}
                        isCurrent={selectedLink === "HOME"}
                        iconClassName={`bi bi-house-fill ${selectedLink === "HOME" ? "dk-blue" : "white"}`}
                    >
                        <span className={`expanded-nav-link ${selectedLink === "HOME" ? "dk-blue" : "white"}`}>
                            Home
                        </span>
                    </NavItemButton>
                </li>

                {/* Report Card Summary */}
                <li>
                    <NavItemButton
                        className={`expanded-nav-row ${isSummaryActive() ? "selected-row" : "not-selected-row"}`}
                        onClick={() => handleOnClick("HOME/SUMMARY")}
                        isCurrent={selectedLink === "HOME/SUMMARY"}
                        iconClassName={`bi bi-card-checklist ${isSummaryActive() ? "dk-blue" : "white"}`}
                    >
                        <span className={`expanded-nav-link ${isSummaryActive() ? "dk-blue" : "white"}`}>
                            Report Card Summary
                        </span>
                    </NavItemButton>

                    {/* Summary sub-nav */}
                    {showSummary && (
                        <ul className="nav-sub-list" aria-label="Report Card Summary options">
                            <li>
                                <NavItemButton
                                    className={`expanded-nav-sub-row ${
                                        selectedLink.includes("HOME/SUMMARY/ROSTER") && !selectedLink.includes("GROUPS")
                                            ? "selected-row"
                                            : "not-selected-row"
                                    }`}
                                    onClick={() => handleOnClick("HOME/SUMMARY/ROSTER")}
                                    isCurrent={
                                        selectedLink.includes("HOME/SUMMARY/ROSTER") && !selectedLink.includes("GROUPS")
                                    }
                                    iconClassName={`bi bi-person-fill ${
                                        selectedLink.includes("HOME/SUMMARY/ROSTER") && !selectedLink.includes("GROUPS")
                                            ? "dk-blue"
                                            : "white"
                                    }`}
                                >
                                    <span
                                        className={`expanded-nav-link ${
                                            selectedLink.includes("HOME/SUMMARY/ROSTER") &&
                                            !selectedLink.includes("GROUPS")
                                                ? "dk-blue"
                                                : "white"
                                        }`}
                                    >
                                        Manage Roster
                                    </span>
                                </NavItemButton>
                            </li>
                            <li>
                                <NavItemButton
                                    className={`expanded-nav-sub-row ${
                                        selectedLink.includes("HOME/SUMMARY/ROSTER/GROUPS")
                                            ? "selected-row"
                                            : "not-selected-row"
                                    }`}
                                    onClick={() => handleOnClick("HOME/SUMMARY/ROSTER/GROUPS")}
                                    isCurrent={selectedLink.includes("HOME/SUMMARY/ROSTER/GROUPS")}
                                    iconClassName={`bi bi-people-fill ${
                                        selectedLink.includes("HOME/SUMMARY/ROSTER/GROUPS") ? "dk-blue" : "white"
                                    }`}
                                >
                                    <span
                                        className={`expanded-nav-link ${
                                            selectedLink.includes("HOME/SUMMARY/ROSTER/GROUPS") ? "dk-blue" : "white"
                                        }`}
                                    >
                                        Manage Group(s)
                                    </span>
                                </NavItemButton>
                            </li>
                            <li>
                                <NavItemButton
                                    className={`expanded-nav-sub-row ${
                                        selectedLink.includes("HOME/SUMMARY/SHARE")
                                            ? "selected-row"
                                            : "not-selected-row"
                                    }`}
                                    onClick={handleManageSharesClick}
                                    isCurrent={selectedLink.includes("HOME/SUMMARY/SHARE")}
                                    iconClassName={`bi bi-box-arrow-up-right ${
                                        selectedLink.includes("HOME/SUMMARY/SHARE") ? "dk-blue" : "white"
                                    }`}
                                >
                                    <span
                                        className={`expanded-nav-link ${
                                            selectedLink.includes("HOME/SUMMARY/SHARE") ? "dk-blue" : "white"
                                        }`}
                                    >
                                        Manage Shares
                                    </span>
                                </NavItemButton>
                            </li>
                        </ul>
                    )}

                    {/* Manage Differentiation - always show */}
                    {showDifferentiation && (
                        <ul className="nav-sub-list" aria-label="Differentiation options">
                            <li>
                                <NavItemButton
                                    className={`expanded-nav-sub-row ${
                                        isDifferentiationSelected ? "selected-row" : "not-selected-row"
                                    }`}
                                    onClick={() => handleOnClick("HOME/SUMMARY/DIFFERENTIATION")}
                                    isCurrent={isDifferentiationSelected}
                                    iconClassName={`bi bi-sliders ${isDifferentiationSelected ? "dk-blue" : "white"}`}
                                >
                                    <span
                                        className={`expanded-nav-link ${
                                            isDifferentiationSelected ? "dk-blue" : "white"
                                        }`}
                                    >
                                        Manage Differentiation
                                    </span>
                                </NavItemButton>
                            </li>
                        </ul>
                    )}
                </li>

                {/* Input Ratings */}
                {!isHistoricalSemester && (
                    <li>
                        <NavItemButton
                            className={`expanded-nav-row ${
                                selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS")
                                    ? "selected-row"
                                    : "not-selected-row"
                            }`}
                            onClick={() => handleInputRatingsClick("HOME/SUMMARY/INPUT-RATINGS")}
                            isCurrent={selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS")}
                            iconClassName={`bi bi-table ${
                                selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS") ? "dk-blue" : "white"
                            }`}
                            ariaExpanded={selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS")}
                        >
                            <span
                                className={`expanded-nav-link ${
                                    selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS") ? "dk-blue" : "white"
                                }`}
                            >
                                Input Ratings By:
                            </span>
                        </NavItemButton>

                        <ul className="nav-sub-sub-list" aria-label="Input Ratings options">
                            {gradingProfilesCurrentSem?.length > 0 && (
                                <li>
                                    <NavItemButton
                                        className={`expanded-nav-sub-row ${
                                            selectedLink === "HOME/SUMMARY/INPUT-RATINGS/HOMEROOM" ||
                                            selectedLink === "HOME/SUMMARY/INPUT-RATINGS"
                                                ? "selected-row"
                                                : "not-selected-row"
                                        }`}
                                        onClick={() => handleOnClick("HOME/SUMMARY/INPUT-RATINGS/HOMEROOM")}
                                        isCurrent={
                                            selectedLink === "HOME/SUMMARY/INPUT-RATINGS/HOMEROOM" ||
                                            selectedLink === "HOME/SUMMARY/INPUT-RATINGS"
                                        }
                                        iconClassName={`bi bi-circle-fill tiny-icon ${
                                            selectedLink === "HOME/SUMMARY/INPUT-RATINGS/HOMEROOM" ||
                                            selectedLink === "HOME/SUMMARY/INPUT-RATINGS"
                                                ? "dk-blue"
                                                : "white"
                                        }`}
                                    >
                                        <span
                                            className={`expanded-nav-link ${
                                                selectedLink === "HOME/SUMMARY/INPUT-RATINGS/HOMEROOM" ||
                                                selectedLink === "HOME/SUMMARY/INPUT-RATINGS"
                                                    ? "dk-blue"
                                                    : "white"
                                            }`}
                                        >
                                            Homeroom
                                        </span>
                                    </NavItemButton>
                                </li>
                            )}

                            <li>
                                <NavItemButton
                                    className={`expanded-nav-sub-row ${
                                        selectedLink === "HOME/SUMMARY/INPUT-RATINGS/GROUPS"
                                            ? "selected-row"
                                            : "not-selected-row"
                                    }`}
                                    onClick={() => handleOnClick("HOME/SUMMARY/INPUT-RATINGS/GROUPS")}
                                    isCurrent={selectedLink === "HOME/SUMMARY/INPUT-RATINGS/GROUPS"}
                                    iconClassName={`bi bi-circle-fill tiny-icon ${
                                        selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS/GROUPS") ? "dk-blue" : "white"
                                    }`}
                                >
                                    <span
                                        className={`expanded-nav-link ${
                                            selectedLink === "HOME/SUMMARY/INPUT-RATINGS/GROUPS" ? "dk-blue" : "white"
                                        }`}
                                    >
                                        Groups
                                    </span>
                                </NavItemButton>
                            </li>

                            {showStudent && (
                                <li>
                                    <NavItemButton
                                        className={`expanded-nav-sub-row ${
                                            selectedLink === "HOME/SUMMARY/INPUT-RATINGS/STUDENT"
                                                ? "selected-row"
                                                : "not-selected-row"
                                        }`}
                                        onClick={() =>
                                            handleOnClick("HOME/SUMMARY/INPUT-RATINGS/STUDENT", firstStudentNumber)
                                        }
                                        isCurrent={selectedLink === "HOME/SUMMARY/INPUT-RATINGS/STUDENT"}
                                        iconClassName={`bi bi-circle-fill tiny-icon ${
                                            selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS/STUDENT")
                                                ? "dk-blue"
                                                : "white"
                                        }`}
                                    >
                                        <span
                                            className={`expanded-nav-link ${
                                                selectedLink === "HOME/SUMMARY/INPUT-RATINGS/STUDENT"
                                                    ? "dk-blue"
                                                    : "white"
                                            }`}
                                        >
                                            Student
                                        </span>
                                    </NavItemButton>
                                </li>
                            )}
                        </ul>
                    </li>
                )}
                {/* Student Search */}
                <li>
                    <NavItemButton
                        className={`expanded-nav-row ${
                            selectedLink === "HOME/STUDENT-SEARCH" ? "selected-row" : "not-selected-row"
                        }`}
                        onClick={() => handleOnClick("HOME/STUDENT-SEARCH")}
                        isCurrent={selectedLink === "HOME/STUDENT-SEARCH"}
                        iconClassName={`bi bi-search ${selectedLink === "HOME/STUDENT-SEARCH" ? "dk-blue" : "white"}`}
                    >
                        <span
                            className={`expanded-nav-link ${
                                selectedLink === "HOME/STUDENT-SEARCH" ? "dk-blue" : "white"
                            }`}
                        >
                            Student Search
                        </span>
                    </NavItemButton>
                </li>
            </ul>
            {isHistoricalSemester && (
                <div className="historical-semester-note">
                    <i className="bi bi-info-circle" aria-hidden="true" />
                    <span>
                        You&apos;re viewing 1st Semester information. Additional navigation items are available when
                        viewing 2nd Semester.
                    </span>
                </div>
            )}
        </nav>
    ) : (
        <nav className="collapsed-nav-container" aria-label="Primary navigation">
            <ul className="nav-list">
                {/* Expand control */}
                <li className="collapsed-nav-row">
                    <button
                        type="button"
                        className="nav-toggle-button"
                        onClick={() => dispatch({ type: "TOGGLE_NAV" })}
                        aria-label="Expand navigation"
                        aria-expanded={!navCollapsed}
                    >
                        <i className="bi bi-arrow-bar-right white arrow-icon" aria-hidden="true" />
                    </button>
                </li>

                {/* Home */}
                <li>
                    <button
                        type="button"
                        className={`collapsed-nav-row ${selectedLink === "HOME" ? "selected-row" : "not-selected-row"}`}
                        onClick={() => handleOnClick("HOME")}
                        aria-label="Home"
                        aria-current={selectedLink === "HOME" ? "page" : undefined}
                    >
                        <i
                            className={`bi bi-house-fill ${selectedLink === "HOME" ? "dk-blue" : "white"}`}
                            aria-hidden="true"
                        />
                    </button>
                </li>

                {/* Summary + subnav */}
                <li>
                    <button
                        type="button"
                        className={`collapsed-nav-row ${summaryRowClass}`}
                        onClick={() => handleOnClick("HOME/SUMMARY")}
                        aria-label="Report Card Summary"
                        aria-current={selectedLink === "HOME/SUMMARY" ? "page" : undefined}
                        aria-expanded={selectedLink.includes("HOME/SUMMARY")}
                    >
                        <i
                            className={`bi bi-card-checklist ${selectedLink === "HOME/SUMMARY" ? "dk-blue" : "white"}`}
                            aria-hidden="true"
                        />
                    </button>

                    {/* Show the icon sub-items only when in summary */}
                    {showSummary && selectedLink.includes("HOME/SUMMARY") && (
                        <ul className="nav-sub-list" aria-label="Report Card Summary options">
                            <li>
                                <button
                                    type="button"
                                    className={`collapsed-sub-nav-row ${
                                        selectedLink.includes("HOME/SUMMARY/ROSTER")
                                            ? "selected-row"
                                            : "not-selected-row"
                                    }`}
                                    onClick={() => handleOnClick("HOME/SUMMARY/ROSTER")}
                                    aria-label="Report Card Roster"
                                    aria-current={selectedLink.includes("HOME/SUMMARY/ROSTER") ? "page" : undefined}
                                >
                                    <i
                                        className={`bi bi-people small-icon ${
                                            selectedLink.includes("HOME/SUMMARY/ROSTER") ? "dk-blue" : "white"
                                        }`}
                                        aria-hidden="true"
                                    />
                                </button>
                            </li>

                            <li>
                                <button
                                    type="button"
                                    className={`collapsed-sub-nav-row ${
                                        selectedLink.includes("HOME/SUMMARY/SHARE")
                                            ? "selected-row"
                                            : "not-selected-row"
                                    }`}
                                    onClick={handleManageSharesClick}
                                    aria-label="Manage Shares"
                                    aria-current={selectedLink.includes("HOME/SUMMARY/SHARE") ? "page" : undefined}
                                >
                                    <i
                                        className={`bi bi-box-arrow-up-right small-icon ${
                                            selectedLink.includes("HOME/SUMMARY/SHARE") ? "dk-blue" : "white"
                                        }`}
                                        aria-hidden="true"
                                    />
                                </button>
                            </li>

                            {/* Manage Differentiation - always show when not in summary */}
                            {showDifferentiation && (
                                <ul className="nav-sub-list" aria-label="Differentiation options">
                                    <li>
                                        <button
                                            type="button"
                                            className={`collapsed-sub-nav-row ${
                                                selectedLink.includes("HOME/SUMMARY/DIFFERENTIATION")
                                                    ? "selected-row"
                                                    : "not-selected-row"
                                            }`}
                                            onClick={() => handleOnClick("HOME/SUMMARY/DIFFERENTIATION")}
                                            aria-label="Manage Differentiation"
                                            aria-current={
                                                selectedLink.includes("HOME/SUMMARY/DIFFERENTIATION")
                                                    ? "page"
                                                    : undefined
                                            }
                                        >
                                            <i
                                                className={`bi bi-sliders small-icon ${
                                                    selectedLink.includes("HOME/SUMMARY/DIFFERENTIATION")
                                                        ? "dk-blue"
                                                        : "white"
                                                }`}
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </li>
                                </ul>
                            )}
                        </ul>
                    )}
                </li>

                {/* Input Ratings */}
                {!isHistoricalSemester && (
                    <li>
                        <button
                            type="button"
                            className={`collapsed-nav-row ${
                                selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS")
                                    ? "selected-row"
                                    : "not-selected-row"
                            }`}
                            onClick={() => handleInputRatingsClick("HOME/SUMMARY/INPUT-RATINGS")}
                            aria-label="Input Ratings By"
                            aria-current={selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS") ? "page" : undefined}
                        >
                            <i
                                className={`bi bi-table ${
                                    selectedLink.includes("HOME/SUMMARY/INPUT-RATINGS") ? "dk-blue" : "white"
                                }`}
                                aria-hidden="true"
                            />
                        </button>
                    </li>
                )}

                {/* Student Search */}

                <li>
                    <button
                        type="button"
                        className={`collapsed-nav-row ${
                            selectedLink === "HOME/STUDENT-SEARCH" ? "selected-row" : "not-selected-row"
                        }`}
                        onClick={() => handleOnClick("HOME/STUDENT-SEARCH")}
                        aria-label="Student Search"
                        aria-current={selectedLink === "HOME/STUDENT-SEARCH" ? "page" : undefined}
                    >
                        <i
                            className={`bi bi-search ${selectedLink === "HOME/STUDENT-SEARCH" ? "dk-blue" : "white"}`}
                            aria-hidden="true"
                        />
                    </button>
                </li>
            </ul>
        </nav>
    );
};

TeacherSidebarMenu.propTypes = {
    state: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    basePath: PropTypes.string,
    teacherGuid: PropTypes.string
};

TeacherSidebarMenu.defaultProps = {
    basePath: "",
    teacherGuid: undefined
};

export default TeacherSidebarMenu;
